import { useMemo, useState } from 'react';

import { apiClient } from '@infrastructure/api/client';
import { AdminListRepository } from '@infrastructure/api/AdminListRepository';
import { DataTable, type DataTableColumn } from '@app/components/data-table/DataTable';
import { formatDateTime } from '@app/lib/format';
import { StatusBadge } from '@app/components/StatusBadge';

interface CampaignRow {
  id: string;
  customer_email: string;
  customer_company: string;
  name: string;
  status: string;
  message_type: string;
  total_recipients: number;
  messages_sent: number;
  messages_delivered: number;
  messages_failed: number;
  total_cost: string;
  scheduled_at: string | null;
  created_at: string;
}

const repo = new AdminListRepository<CampaignRow>(apiClient, 'campaigns');

const columns: DataTableColumn<CampaignRow>[] = [
  { id: 'created_at', header: 'Created', accessor: (r) => formatDateTime(r.created_at) },
  { id: 'name', header: 'Name', accessor: 'name' },
  {
    id: 'customer_email', header: 'Customer',
    accessor: (r) => (
      <span>
        {r.customer_company}
        <span className="block text-xs text-slate-500">{r.customer_email}</span>
      </span>
    ),
  },
  { id: 'status', header: 'Status', accessor: (r) => <StatusBadge value={r.status} /> },
  { id: 'message_type', header: 'Type', accessor: 'message_type' },
  { id: 'total_recipients', header: 'Recipients', accessor: 'total_recipients' },
  {
    id: 'progress',
    header: 'Progress',
    accessor: (r) => `${r.messages_sent} sent · ${r.messages_delivered} ok · ${r.messages_failed} fail`,
  },
  { id: 'total_cost', header: 'Cost', accessor: (r) => `$${r.total_cost}` },
];

export function CampaignsPage() {
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');

  const filters = useMemo(() => {
    const f: Record<string, string> = {};
    if (search) f.search = search;
    if (statusFilter) f.status = statusFilter;
    return f;
  }, [search, statusFilter]);

  return (
    <section>
      <div className="mb-3 flex flex-wrap items-end justify-between gap-2">
        <h2 className="text-lg font-semibold">Campaigns</h2>
        <div className="flex flex-wrap gap-2 text-sm">
          <input
            type="search"
            placeholder="Search name or customer"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="rounded border px-2 py-1"
          />
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="rounded border px-2 py-1"
          >
            <option value="">All statuses</option>
            <option value="draft">Draft</option>
            <option value="scheduled">Scheduled</option>
            <option value="running">Running</option>
            <option value="completed">Completed</option>
            <option value="paused">Paused</option>
            <option value="cancelled">Cancelled</option>
          </select>
        </div>
      </div>
      <DataTable<CampaignRow>
        columns={columns}
        fetcher={(p) => repo.list({ ...p, filters })}
        pagination="offset"
        defaultPageSize={25}
        rowKey={(r) => r.id}
        emptyState={<span data-testid="datatable-empty">No campaigns yet.</span>}
      />
    </section>
  );
}
